import React, { useContext, useState, useRef, useCallback } from "react"
import { Link } from "gatsby"
import styled from "styled-components"
import DesignTokens from "./designTokens"
import { LanguageContext } from "../locales/langContext"
import { getMenuApplicationLink } from "./pathUtils"

function getNodeLink(node, locale) {
  return getMenuApplicationLink(node.application, locale)
}

function getNodeLabel(node) {
  if (node.application) {
    return node.title || node.application.title
  }
  return node.title
}

const MenuEntry = ({ link, children }) => {
  if (!link) {
    return <MenuLabel>{children}</MenuLabel>
  }
  if (link.external) {
    return (
      <MenuLink
        as="a"
        href={link.href}
        target={link.newWindow ? "_blank" : undefined}
        rel={link.newWindow ? "noopener noreferrer" : undefined}
      >
        {children}
      </MenuLink>
    )
  }
  return <MenuLink to={link.href}>{children}</MenuLink>
}

const NestedDropdownMenu = ({ anchor, links, path }) => {
  const [showMenu, setShowMenu] = useState(false)
  const [hoveredFirst, setHoveredFirst] = useState(null)
  const [hoveredSecond, setHoveredSecond] = useState(null)
  const rootTimeout = useRef(null)
  const firstTimeout = useRef(null)
  const secondTimeout = useRef(null)
  const isBrowser = typeof window !== "undefined"
  const langContext = useContext(LanguageContext)

  const clearAllTimeouts = useCallback(() => {
    if (rootTimeout.current) clearTimeout(rootTimeout.current)
    if (firstTimeout.current) clearTimeout(firstTimeout.current)
    if (secondTimeout.current) clearTimeout(secondTimeout.current)
  }, [])

  const handleRootEnter = useCallback(() => {
    clearAllTimeouts()
    setShowMenu(true)
  }, [clearAllTimeouts])

  const handleRootLeave = useCallback(() => {
    rootTimeout.current = setTimeout(() => {
      setShowMenu(false)
      setHoveredFirst(null)
      setHoveredSecond(null)
    }, 150)
  }, [])

  const handleFirstEnter = useCallback(
    id => {
      if (firstTimeout.current) clearTimeout(firstTimeout.current)
      if (rootTimeout.current) clearTimeout(rootTimeout.current)
      setHoveredFirst(id)
      setHoveredSecond(null)
    },
    []
  )

  const handleFirstLeave = useCallback(() => {
    firstTimeout.current = setTimeout(() => {
      setHoveredFirst(null)
      setHoveredSecond(null)
    }, 150)
  }, [])

  const handleSecondEnter = useCallback(
    id => {
      if (secondTimeout.current) clearTimeout(secondTimeout.current)
      if (firstTimeout.current) clearTimeout(firstTimeout.current)
      if (rootTimeout.current) clearTimeout(rootTimeout.current)
      setHoveredSecond(id)
    },
    []
  )

  const handleSecondLeave = useCallback(() => {
    secondTimeout.current = setTimeout(() => {
      setHoveredSecond(null)
    }, 150)
  }, [])

  const locale = langContext.locale

  return (
    <Item
      onMouseEnter={handleRootEnter}
      onMouseLeave={handleRootLeave}
    >
      <a
        style={{
          borderBottom:
            isBrowser &&
            window.location.href.indexOf((anchor || "").toLowerCase()) > -1
              ? "2px solid #00793D"
              : "none",
          cursor: "default",
          textDecoration: "none",
        }}
      >
        {anchor}
      </a>
      {showMenu && (
        <Dropdown>
          {links.map(item => {
            const hasChildren =
              item.treeChildren && item.treeChildren.length > 0
            const itemLink = getNodeLink(item, locale)
            const label = getNodeLabel(item)

            return (
              <MenuItem
                key={item.id}
                onMouseEnter={() => handleFirstEnter(item.id)}
                onMouseLeave={handleFirstLeave}
              >
                <MenuEntry link={itemLink}>
                  <span>{label}</span>
                  {hasChildren && <Arrow>&rsaquo;</Arrow>}
                </MenuEntry>

                {hasChildren && hoveredFirst === item.id && (
                  <Flyout>
                    {item.treeChildren.map(child => {
                      const childHasChildren =
                        child.treeChildren && child.treeChildren.length > 0
                      const childLink = getNodeLink(child, locale)
                      const childLabel = getNodeLabel(child)

                      return (
                        <MenuItem
                          key={child.id}
                          onMouseEnter={() => handleSecondEnter(child.id)}
                          onMouseLeave={handleSecondLeave}
                        >
                          <MenuEntry link={childLink}>
                            <span>{childLabel}</span>
                            {childHasChildren && <Arrow>&rsaquo;</Arrow>}
                          </MenuEntry>

                          {childHasChildren && hoveredSecond === child.id && (
                            <Flyout>
                              {child.treeChildren.map(grandChild => {
                                const grandChildLink = getNodeLink(
                                  grandChild,
                                  locale
                                )
                                const grandChildLabel =
                                  getNodeLabel(grandChild)

                                return (
                                  <MenuItem key={grandChild.id}>
                                    <MenuEntry link={grandChildLink}>
                                      <span>{grandChildLabel}</span>
                                    </MenuEntry>
                                  </MenuItem>
                                )
                              })}
                            </Flyout>
                          )}
                        </MenuItem>
                      )
                    })}
                  </Flyout>
                )}
              </MenuItem>
            )
          })}
        </Dropdown>
      )}
    </Item>
  )
}

const Item = styled.li`
  position: relative;
  margin-bottom: 0;
  padding: 1rem 0;
  &:last-child {
    & > a {
      padding-right: 0;
    }
  }
  a {
    text-decoration: none;
  }
`

const Dropdown = styled.ul`
  margin-left: 0;
  margin-top: 1rem;
  text-align: left;
  background: ${DesignTokens.colors.white};
  position: absolute;
  display: flex;
  flex-direction: column;
  min-width: 260px;
  z-index: 100;
  box-shadow: ${DesignTokens.shadows.overlay};
  padding: 0;
  list-style: none;
`

const Flyout = styled.ul`
  position: absolute;
  top: 0;
  left: 100%;
  background: ${DesignTokens.colors.white};
  min-width: 260px;
  z-index: 101;
  box-shadow: ${DesignTokens.shadows.overlay};
  margin: 0;
  padding: 0;
  list-style: none;
`

const MenuItem = styled.li`
  position: relative;
  margin: 0;
  display: flex;
  flex-direction: column;
`

const MenuLink = styled(Link)`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: ${DesignTokens.spacing[2]} ${DesignTokens.spacing[4]};
  color: ${DesignTokens.colors.primary[500]};
  text-decoration: none !important;
  white-space: nowrap;
  &:hover {
    color: white;
    background-color: ${DesignTokens.colors.primary[500]};
  }
`

const MenuLabel = styled.span`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: ${DesignTokens.spacing[2]} ${DesignTokens.spacing[4]};
  color: ${DesignTokens.colors.primary[500]};
  cursor: default;
  white-space: nowrap;
  &:hover {
    color: white;
    background-color: ${DesignTokens.colors.primary[500]};
  }
`

const Arrow = styled.span`
  margin-left: 0.75rem;
  font-size: 1.2em;
  line-height: 1;
`

export default NestedDropdownMenu
